import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useGlobalContext } from "../../ContextApi/GlobalContext";
import { registerAdmin } from "../../Backend/authFunction";
import { getDashboardData } from "../../Backend/dashboardfunction";
import {
  pageShellClass,
  pageShellLoadingClass,
} from "../../Container/PageShell/pageShellClasses";
import Swal from "sweetalert2";

// ✅ 1. Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const MasterDashboard = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useGlobalContext();
  const [loading, setLoading] = useState(true);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ 2. Chart Filter State
  const [chartFilter, setChartFilter] = useState("7d");

  // --- DATA SYNCHRONIZATION ---
  useEffect(() => {
    const syncDashboard = async () => {
      setLoading(true);
      const result = await getDashboardData();
      if (result.success) {
        dispatch({ dashboardData: result.data });
      } else {
        console.error("Dashboard Sync Failed:", result.error);
      }
      setLoading(false);
    };
    syncDashboard();
  }, []);

  // --- DATA MAPPING ---
  const dashboard = state.dashboardData || {};
  const admin = state.adminData || {};
  const adminName = admin.name || "Master Admin";

  const transactions = dashboard.transactions || [];
  const adminLogs = dashboard.adminActivities || [];
  const notifications = dashboard.notifications || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const dashStats = dashboard.stats || {};

  // ✅ 3. STATS GRID WITH ROUTING LINKS
  const statsGrid = [
    {
      title: "System Admins",
      value: dashStats.adminsCount || 0,
      icon: "admin_panel_settings",
      color: "navy",
      link: null,
    },
    {
      title: "Total Users",
      value: dashStats.usersCount || 0,
      icon: "group",
      color: "rose",
      link: "/users",
    },
    {
      title: "Transactions",
      value: dashStats.transactionsCount || 0,
      icon: "payments",
      color: "navy",
      link: "/users",
    },
    {
      title: "Support Tickets",
      value: dashStats.activeTicketsCount || 0,
      icon: "confirmation_number",
      color: "rose",
      link: "/support",
    },
    {
      title: "Logged Bugs",
      value: dashStats.openBugsCount || 0,
      icon: "bug_report",
      color: "navy",
      link: "/bugs-report",
    },
    {
      title: "User Queries",
      value: dashStats.queriesCount || 0,
      icon: "question_answer",
      color: "rose",
      link: "/queries",
    },
  ];

  // ✅ 4. NOTIFICATION ROUTING HANDLER
  const handleNotificationClick = (type) => {
    const routeMap = {
      bug: "/bugs-report",
      ticket: "/support",
      message: "/support",
      user: "/users",
      payment: "/users",
      enquiry: "/queries",
    };
    const targetRoute = routeMap[type] || "/dashboard";
    setIsNotifOpen(false);
    navigate(targetRoute);
  };

  // ✅ 5. DYNAMIC CHART DATA GENERATION
  const chartData = useMemo(() => {
    const now = new Date();
    let daysToSubtract = 7;
    let groupByMonth = false;

    // Filter Logic Mapping
    switch (chartFilter) {
      case "15d":
        daysToSubtract = 15;
        break;
      case "30d":
        daysToSubtract = 30;
        break;
      case "3m":
        daysToSubtract = 90;
        groupByMonth = true;
        break;
      case "6m":
        daysToSubtract = 180;
        groupByMonth = true;
        break;
      case "12m":
        daysToSubtract = 365;
        groupByMonth = true;
        break;
      default:
        daysToSubtract = 7;
        break; // 7d
    }

    const startDate = new Date(
      now.getTime() - daysToSubtract * 24 * 60 * 60 * 1000,
    );
    const dateBuckets = {};
    const labels = [];

    // Initialize buckets
    if (groupByMonth) {
      for (let i = daysToSubtract / 30; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const label = d.toLocaleString("default", {
          month: "short",
          year: "2-digit",
        });
        if (!dateBuckets[label]) {
          dateBuckets[label] = { users: 0, tx: 0, activity: 0 };
          labels.push(label);
        }
      }
    } else {
      for (let i = daysToSubtract - 1; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const label = d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        dateBuckets[label] = { users: 0, tx: 0, activity: 0 };
        labels.push(label);
      }
    }

    // Helper to bucket dates safely
    const addToBucket = (dateString, type) => {
      if (!dateString) return;
      const d = new Date(dateString);
      if (d < startDate) return;

      const label = groupByMonth
        ? d.toLocaleString("default", { month: "short", year: "2-digit" })
        : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

      if (dateBuckets[label]) dateBuckets[label][type]++;
    };

    // Aggregate Users
    Object.values(dashboard.companies || {}).forEach((c) =>
      addToBucket(c.createdAt, "users"),
    );
    // Aggregate Transactions
    transactions.forEach((tx) => addToBucket(tx.timestamp || tx.date, "tx"));
    // Aggregate Activity (Tickets + Bugs + Queries)
    Object.values(dashboard.tickets || {}).forEach((t) =>
      addToBucket(t.createdAt, "activity"),
    );
    Object.values(dashboard.bugReports || {}).forEach((b) =>
      addToBucket(b.createdAt, "activity"),
    );
    Object.values(dashboard.responses || {}).forEach((q) =>
      addToBucket(q.createdAt, "activity"),
    );

    return {
      labels,
      datasets: [
        {
          label: "New Users",
          data: labels.map((l) => dateBuckets[l].users),
          backgroundColor: "#0B1C2D",
          borderRadius: 4,
          barPercentage: 0.6,
        },
        {
          label: "Transactions",
          data: labels.map((l) => dateBuckets[l].tx),
          backgroundColor: "#E63946",
          borderRadius: 4,
          barPercentage: 0.6,
        },
        {
          label: "Activity (Support/Bugs)",
          data: labels.map((l) => dateBuckets[l].activity),
          backgroundColor: "#94A3B8",
          borderRadius: 4,
          barPercentage: 0.6,
        },
      ],
    };
  }, [dashboard, chartFilter]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          font: { size: 10, weight: "bold" },
        },
      },
      tooltip: {
        backgroundColor: "#0B1C2D",
        padding: 12,
        titleFont: { size: 11 },
        bodyFont: { size: 12, weight: "bold" },
        displayColors: true,
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: "#F1F5F9" },
        ticks: {
          precision: 0,
          color: "#94A3B8",
          font: { size: 10, weight: "bold" },
        },
      },
    },
    interaction: { mode: "index", intersect: false },
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.target);
    const result = await registerAdmin(formData);
    setIsSubmitting(false);
    if (result.success) {
      Swal.fire({
        icon: "success",
        title: "Admin Provisioned",
        text: result.message,
        customClass: { popup: "rounded-3xl" },
      });
      setIsAdminModalOpen(false);
    } else {
      Swal.fire({
        icon: "error",
        title: "Provisioning Failed",
        text: result.message,
      });
    }
  };

  if (loading) {
    return (
      <div className={pageShellLoadingClass}>
        <div className="size-14 border-4 border-[#0B1C2D]/5 border-t-[#E63946] rounded-full animate-spin mb-6" />
        <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400 animate-pulse">
          Establishing Secure Uplink
        </h2>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      <div className="p-4 sm:p-6 lg:px-10 lg:py-6 mx-auto max-w-[1600px]">
        {/* --- HEADER --- */}
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 lg:mb-10 gap-4 sticky top-[calc(3.5rem+env(safe-area-inset-top))] lg:top-0 bg-[#F6F8FB]/95 backdrop-blur-xl z-40 py-3 lg:py-4 border-b border-slate-200 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
          <div className="hidden lg:block">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="size-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]"></span>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em]">
                Root Active
              </span>
            </div>
            <h1 className="text-2xl font-black text-[#0B1C2D] tracking-tighter uppercase">
              Command Center
            </h1>
          </div>

          <div className="flex items-center gap-3 w-full lg:w-auto justify-between lg:justify-end">
            {/* NOTIFICATION HUB */}
            <div className="relative">
              <button
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                className="relative p-2.5 bg-white rounded-xl border border-slate-200 text-[#0B1C2D] hover:text-[#E63946] transition-all"
              >
                <span className="material-symbols-outlined text-2xl">
                  notifications
                </span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 size-4 bg-[#E63946] text-white text-[8px] font-bold flex items-center justify-center rounded-full border-2 border-[#F6F8FB]">
                    {unreadCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {isNotifOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsNotifOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-4 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50"
                    >
                      <div className="p-4 bg-[#0B1C2D] text-white flex justify-between items-center">
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          System Alerts
                        </span>
                        <span className="text-[9px] font-bold bg-[#E63946] px-2 py-0.5 rounded-full uppercase">
                          Live
                        </span>
                      </div>

                      {/* ✅ CLICKABLE NOTIFICATIONS */}
                      <div className="max-h-[350px] overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.map((n, i) => (
                            <div
                              key={i}
                              onClick={() => handleNotificationClick(n.type)}
                              className="p-4 hover:bg-slate-50 border-b border-slate-50 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-bold text-[#0B1C2D]">
                                  {n.title}
                                </p>
                                {!n.isRead && (
                                  <span className="size-2 bg-[#E63946] rounded-full"></span>
                                )}
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1">
                                {n.time}
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="py-12 text-center text-slate-300 text-[10px] font-black uppercase tracking-widest">
                            No Signals Detected
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => setIsAdminModalOpen(true)}
              className="flex items-center gap-2 bg-[#E63946] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-red-900/20 hover:bg-[#c9303c] transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-sm">
                add_moderator
              </span>
              <span className="hidden xs:inline sm:inline">Provision Admin</span>
              <span className="sm:hidden">Admin</span>
            </button>

            <div
              onClick={() => navigate("/myprofile")}
              className="hidden lg:flex w-10 h-10 rounded-xl bg-[#0B1C2D] text-white items-center justify-center font-black text-xs cursor-pointer hover:scale-105 transition-transform"
            >
              {adminName.charAt(0)}
            </div>
          </div>
        </header>

        {/* --- STATS GRID WITH ROUTING --- */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-6 mb-8 lg:mb-12">
          {statsGrid.map((stat, i) => (
            <div
              key={i}
              onClick={() => stat.link && navigate(stat.link)}
              className={`bg-white p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm transition-all active:scale-[0.98] ${stat.link ? "cursor-pointer hover:shadow-md hover:border-slate-300 hover:-translate-y-1" : ""}`}
            >
              <div
                className={`size-10 rounded-xl mb-4 flex items-center justify-center ${stat.color === "rose" ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#0B1C2D]/10 text-[#0B1C2D]"}`}
              >
                <span className="material-symbols-outlined text-xl">
                  {stat.icon}
                </span>
              </div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                {stat.title}
              </p>
              <h3 className="text-xl font-black text-[#0B1C2D] tracking-tighter">
                {stat.value}
              </h3>
            </div>
          ))}
        </div>

        {/* --- MONITORING GRID --- */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
          <div className="xl:col-span-2 space-y-8">
            {/* ✅ DYNAMIC FILTER CHART */}
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <h3 className="text-[10px] font-black text-[#0B1C2D] uppercase tracking-widest">
                  System Signal Load
                </h3>
                <select
                  value={chartFilter}
                  onChange={(e) => setChartFilter(e.target.value)}
                  className="bg-slate-50 border border-slate-200 text-[#0B1C2D] text-[10px] font-bold rounded-lg px-3 py-1.5 outline-none cursor-pointer"
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="15d">Last 15 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="3m">Last 3 Months</option>
                  <option value="6m">Last 6 Months</option>
                  <option value="12m">Last 12 Months</option>
                </select>
              </div>
              <div className="h-64 relative w-full">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>

            {/* TRANSACTION FEED */}
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
              <h3 className="text-[10px] font-black text-[#0B1C2D] uppercase tracking-widest mb-8">
                Transaction Intelligence Feed
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest text-left border-b border-slate-50">
                      <th className="pb-4">Reference ID</th>
                      <th className="pb-4">User Node</th>
                      <th className="pb-4">Amount</th>
                      <th className="pb-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? (
                      transactions.slice(0, 10).map((tx, i) => (
                        <tr
                          key={i}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="py-4 text-xs font-bold text-[#0B1C2D]">
                            #{tx.id || "TX-NODE"}
                          </td>
                          <td className="py-4 text-xs text-slate-500 font-medium">
                            <div className="flex flex-col">
                              <span>{tx.userName || "Root User"}</span>
                              <span className="text-[8px] text-slate-300 font-black uppercase tracking-tighter">
                                {tx.userEmail}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 text-xs font-black text-[#0B1C2D]">
                            ₹{tx.amount}
                          </td>
                          <td className="py-4">
                            <span
                              className={`px-2.5 py-0.5 text-[8px] font-black rounded-full uppercase ${tx.status === "success" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}
                            >
                              {tx.status || "Live"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="py-16 text-center text-[9px] font-black text-slate-300 uppercase tracking-widest"
                        >
                          No Transaction Signal Detected
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* AUDIT LOG */}
          <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm sticky top-28">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-[10px] font-black text-[#0B1C2D] uppercase tracking-widest">
                Infrastructure Audit Log
              </h3>
              <span className="size-1.5 bg-[#E63946] rounded-full animate-pulse"></span>
            </div>
            <div className="space-y-8 relative before:absolute before:left-3 before:top-2 before:bottom-2 before:w-[1px] before:bg-slate-100">
              {adminLogs.length > 0 ? (
                adminLogs.map((log, i) => (
                  <div key={i} className="relative pl-10 group">
                    <div className="absolute left-0 top-0 size-6 bg-white border border-slate-200 rounded-lg flex items-center justify-center text-slate-400 group-hover:border-[#E63946] group-hover:text-[#E63946] transition-all">
                      <span className="material-symbols-outlined text-[14px]">
                        {log.icon || "terminal"}
                      </span>
                    </div>
                    <p className="text-[11px] font-bold text-[#0B1C2D] leading-tight group-hover:text-[#E63946] transition-colors">
                      {log.title}
                    </p>
                    <p className="text-[8px] font-black text-slate-400 mt-0.5 uppercase tracking-tighter">
                      {log.time}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-300 text-[10px] font-black uppercase">
                  No Recent Infrastructure Changes
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ADMIN PROVISIONING MODAL */}
      <AnimatePresence>
        {isAdminModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[#0B1C2D]/30 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-3xl shadow-2xl border border-white overflow-hidden"
            >
              <header className="bg-[#0B1C2D] p-6 text-white flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-black uppercase tracking-tight">
                    Provision Admin
                  </h3>
                  <p className="text-slate-400 text-[8px] font-bold uppercase tracking-widest mt-1">
                    Assign Master Credentials
                  </p>
                </div>
                <button
                  onClick={() => setIsAdminModalOpen(false)}
                  className="hover:bg-white/10 p-2 rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </header>
              <form onSubmit={handleAddAdmin} className="p-8 space-y-5">
                <input
                  name="fullName"
                  required
                  placeholder="Full Legal Name"
                  className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none font-bold text-sm transition-all"
                />
                <input
                  name="email"
                  required
                  type="email"
                  placeholder="Admin Email"
                  className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none font-bold text-sm transition-all"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    name="adminRole"
                    required
                    placeholder="Role"
                    className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none font-bold text-sm"
                  />
                  <input
                    name="phone"
                    required
                    placeholder="Mobile"
                    className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none font-bold text-sm"
                  />
                </div>
                <input
                  name="password"
                  required
                  type="password"
                  placeholder="Master Key"
                  className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none font-bold text-sm"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#0B1C2D] text-white font-black rounded-xl uppercase tracking-widest hover:bg-[#E63946] transition-all"
                >
                  {isSubmitting ? "Processing..." : "Grant Access"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MasterDashboard;
